import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { ChevronDown, ChevronUp, ArrowRightLeft, Ticket, Bot, UserPlus, LogOut, Sparkles } from 'lucide-react'
import { useLiveChat, type LiveChatConversation } from '../../../hooks/useLiveChat.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useConversationsStore } from '../../../store/conversationsStore.js'
import { useAuthStore } from '../../../store/authStore.js'
import { getSocket } from '../../../socket/client.js'
import { api } from '../../../api/client.js'
import MessageBubble from '../../../modules/conversations/MessageBubble.js'
import MessageComposer from '../../../modules/conversations/MessageComposer.js'
import { useScrollAnchor } from '../../../modules/conversations/ScrollAnchor.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import { ToolButton } from '../../../components/conversations/detail/primitives.js'
import type { Message } from '../../../modules/conversations/types.js'
import { ChatSummaryDialog } from './ChatSummaryDialog.js'

export interface LiveChatThreadProps {
  chatId: string | null
  onOpenDetails?: () => void
  onOpenTransfer?: () => void
  isAssignedToMe?: boolean
  isAiAssigned?: boolean
  aiAgentName?: string | null
  onChanged?: () => void
}

function isReassignmentNotice(text?: string): boolean {
  return text != null && /^Reassigned to .+ \(previous agent went offline\)$/.test(text)
}

function isSystemOrDivider(msg: Message): boolean {
  return (
    isReassignmentNotice(msg.body) ||
    (msg.direction as string) === 'system' ||
    (typeof msg.body === 'string' && /^Chat (resumed|restarted)/i.test(msg.body))
  )
}

interface MessageGroup {
  conversationId: string
  conversation: LiveChatConversation | undefined
  messages: Message[]
}

export default function LiveChatThread({ chatId, onOpenDetails, onOpenTransfer, isAssignedToMe = true, isAiAssigned, aiAgentName, onChanged }: LiveChatThreadProps): JSX.Element {
  const navigate = useNavigate()
  const toast = useToast()
  const { chat, messages, conversations, loading, loadingMore, hasMore, error, appendMessage, replaceMessage, markMessageFailed, patchMessage, refetch, loadMore, updateStatus } =
    useLiveChat(chatId)
  const [isVisitorTyping, setIsVisitorTyping] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const [finalizeError, setFinalizeError] = useState<string | null>(null)
  const [ending, setEnding] = useState(false)
  const [finalized, setFinalized] = useState<{ conversationId: string; referenceId: string } | null>(null)
  const [collapsedConversations, setCollapsedConversations] = useState<Set<string>>(() => new Set())
  const [summaryOpen, setSummaryOpen] = useState(false)

  const handleEndChat = useCallback(() => {
    if (!chatId || chat?.status !== 'active') return
    setEnding(true)
    api
      .post(`/agent/live-chats/${chatId}/end`, {})
      .then(() => {
        toast.success('Chat ended')
        updateStatus('abandoned')
        window.dispatchEvent(new CustomEvent('kc3:chat:ended', { detail: { chatId } }))
        onChanged?.()
      })
      .catch((err: unknown) => {
        const error = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'End chat failed'
        toast.error(error)
      })
      .finally(() => setEnding(false))
  }, [chatId, chat?.status, updateStatus, onChanged, toast])
  const isActiveConversation = (conversationId: string): boolean => conversationId === chatId
  const toggleConversation = (conversationId: string): void => {
    setCollapsedConversations((prev) => {
      const next = new Set(prev)
      if (next.has(conversationId)) next.delete(conversationId)
      else next.add(conversationId)
      return next
    })
  }

  const messageGroups = useMemo<MessageGroup[]>(() => {
    const groups: MessageGroup[] = []
    for (const m of messages) {
      const last = groups[groups.length - 1]
      if (!last || last.conversationId !== m.conversationId) {
        groups.push({
          conversationId: m.conversationId,
          conversation: conversations.find((c) => c.id === m.conversationId),
          messages: [m],
        })
      } else {
        last.messages.push(m)
      }
    }
    return groups
  }, [messages, conversations])

  const unpromotedCount = chatId !== null ? messages.filter((m) => m.conversationId === chatId).length : 0
  const hasUnpromoted = unpromotedCount > 0

  const currentAgentId = useAuthStore((s) => s.user?.sub) ?? null
  const failureReasons = useConversationsStore((s) => s.failureReasonsByMessageId)
  const setFailureReason = useConversationsStore((s) => s.setFailureReason)
  const ratingScale = useConversationsStore((s) => s.ratingScale)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const { atBottom, scrollToBottom, newSinceAtBottom } = useScrollAnchor(messages, containerRef)
  const fetchingMoreRef = useRef(false)

  function handleScroll() {
    if (fetchingMoreRef.current || !hasMore || loadingMore) return
    const el = containerRef.current
    if (!el) return
    if (el.scrollTop <= el.clientHeight * 0.2) {
      const oldScrollHeight = el.scrollHeight
      const oldScrollTop = el.scrollTop
      fetchingMoreRef.current = true
      void loadMore().finally(() => {
        requestAnimationFrame(() => {
          const next = containerRef.current
          if (!next) return
          const added = next.scrollHeight - oldScrollHeight
          if (added > 0) {
            next.scrollTop = oldScrollTop + added
          }
          fetchingMoreRef.current = false
        })
      })
    }
  }

  // Reset the local "just finalized" banner when switching to a different chat.
  useEffect(() => {
    setFinalized(null)
    setFinalizeError(null)
    if (chatId) scrollToBottom()
  }, [chatId, scrollToBottom])

  // Live message append — same pattern ChatThread.tsx (now the finalized-
  // conversation thread) uses; the ws-gateway resolves a live_chats id
  // through the same conversation:message:new event, so this works
  // unchanged for the staging period.
  useEffect(() => {
    if (!chatId) return
    const socket = getSocket()
    socket.emit('conversation:open', { conversationId: chatId })

    const onMessageNew = (data: unknown): void => {
      const e = data as {
        conversationId?: string
        messageId?: string
        direction?: Message['direction']
        lastMessagePreview?: string
        lastMessageAt?: string
        timestamp?: string
        attachments?: { id: string; filename: string; mimeType: string; sizeBytes: number }[]
      }
      if (e.conversationId !== chatId || !e.messageId) return
      const existing = useConversationsStore.getState().messagesByConversationId[chatId] ?? []
      if (existing.some((m) => m.id === e.messageId)) return
      if (e.direction === 'outbound') {
        const firstIncomingAttachmentId = e.attachments?.[0]?.id
        const pendingOwn = existing.find((m) =>
          m.status === 'sending' &&
          m.direction === 'outbound' &&
          (m.body === (e.lastMessagePreview ?? '') ||
            (firstIncomingAttachmentId !== undefined &&
              m.attachments.some((a) => a.id === firstIncomingAttachmentId)))
        )
        if (pendingOwn) {
          replaceMessage(pendingOwn.id, {
            ...pendingOwn,
            id: e.messageId,
            status: 'sent',
            createdAt: e.lastMessageAt ?? e.timestamp ?? pendingOwn.createdAt,
          })
          return
        }
      }
      const isInbound = (e.direction ?? 'inbound') === 'inbound'
      appendMessage({
        id: e.messageId,
        conversationId: chatId,
        direction: e.direction ?? 'inbound',
        senderHandle: isInbound ? null : '',
        senderAgentId: null,
        senderAgentName: null,
        senderName: isInbound ? (chat?.visitorName ?? null) : null,
        body: e.lastMessagePreview ?? '',
        attachments: e.attachments ?? [],
        status: 'sent',
        createdAt: e.lastMessageAt ?? e.timestamp ?? new Date().toISOString(),
      })
    }

    const onMessageStatusChanged = (data: { conversationId: string; messageId: string; status: string }) => {
      if (data.conversationId !== chatId) return
      patchMessage(data.messageId, { status: data.status as Message['status'] })
    }

    const onTypingStart = (data: { conversationId: string }) => {
      if (data.conversationId === chatId) setIsVisitorTyping(true)
    }
    const onTypingStop = (data: { conversationId: string }) => {
      if (data.conversationId === chatId) setIsVisitorTyping(false)
    }

    const onMessageRated = (data: { conversationId: string; messageId: string; rating: 'up' | 'down' | undefined }) => {
      if (data.conversationId === chatId) {
        const patch: Partial<Message> = data.rating === undefined ? {} : { rating: data.rating }
        patchMessage(data.messageId, patch)
      }
    }

    socket.on('conversation:message:new', onMessageNew)
    socket.on('conversation:message:status:changed', onMessageStatusChanged)
    socket.on('conversation:message:rated', onMessageRated)
    socket.on('conversation:typing:start', onTypingStart)
    socket.on('conversation:typing:stop', onTypingStop)
    return () => {
      socket.emit('conversation:close', { conversationId: chatId })
      socket.off('conversation:message:new', onMessageNew)
      socket.off('conversation:message:status:changed', onMessageStatusChanged)
      socket.off('conversation:message:rated', onMessageRated)
      socket.off('conversation:typing:start', onTypingStart)
      socket.off('conversation:typing:stop', onTypingStop)
    }
  }, [chatId, appendMessage, replaceMessage, patchMessage, chat])

  async function handleFinalize(): Promise<void> {
    if (!chatId || !isAssignedToMe) return
    setFinalizing(true)
    setFinalizeError(null)
    try {
      const result = await api.post<{ conversationId: string; referenceId: string }>(`/agent/live-chats/${chatId}/finalize`, {})
      // Finalizing creates a conversation from the current active messages and
      // preserves the live chat for continued chatting. Refresh so the messages
      // are grouped under the newly created conversation.
      setFinalized(result)
      await refetch()
    } catch (err) {
      setFinalizeError((err as Error).message)
    } finally {
      setFinalizing(false)
    }
  }

  if (!chatId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-slate-50 text-slate-400">
        <p className="text-sm">Select a chat to start.</p>
      </div>
    )
  }

  const chConfig = getChannelConfig('chat')
  const ChIcon = chConfig.icon

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      <div className="shrink-0 flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full ${chConfig.bg}`}>
            <ChIcon className={`h-4 w-4 ${chConfig.color}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              <button
                type="button"
                onClick={onOpenDetails}
                className="hover:text-blue-600"
                aria-label="Open conversation details"
              >
                {chat?.visitorName || chat?.visitorId || 'Visitor'}
              </button>
            </h3>
            {isAiAssigned && (
              <span className="flex items-center gap-1 text-xs text-purple-600">
                <Bot className="h-3 w-3" />
                {aiAgentName ?? 'AI Agent'}
              </span>
            )}
            {chat && !isAiAssigned && <span className="text-xs text-slate-400">{finalized ? 'created' : chat.status === 'abandoned' ? 'ended' : chat.status}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {isAiAssigned && !isAssignedToMe && (
            <button
              type="button"
              onClick={() => void api.post(`/agent/live-chats/${chatId}/assign`, {}).then(() => { onChanged?.() }).catch(() => {})}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-all hover:bg-slate-50 active:scale-[0.97]"
            >
              <UserPlus className="h-3.5 w-3.5" />
              Assign to Me
            </button>
          )}
          {chat?.status === 'active' && (
            <button
              type="button"
              onClick={() => void handleFinalize()}
              disabled={!isAssignedToMe || !hasUnpromoted || finalizing}
              title={
                !hasUnpromoted
                  ? 'No unpromoted messages'
                  : `Create a conversation from ${unpromotedCount} unpromoted message${unpromotedCount === 1 ? '' : 's'}`
              }
              className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40"
            >
              <Ticket className="h-3.5 w-3.5" />
              {finalizing ? 'Creating…' : 'Create Conversation'}
              {unpromotedCount > 0 && !finalizing && (
                <span className="rounded-full bg-[#25D366]/15 px-1.5 text-[10px] font-semibold text-[#128C7E]">
                  {unpromotedCount}
                </span>
              )}
            </button>
          )}
          {chatId && (
            <button
              type="button"
              onClick={() => setSummaryOpen(true)}
              title="Summarise this chat in simple words using AI"
              className="flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50/70 px-3 py-1.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 hover:border-indigo-300"
            >
              <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
              Chat Summary
            </button>
          )}
          {(isAssignedToMe || isAiAssigned) && (
            <ToolButton label="Transfer" icon={<ArrowRightLeft className="h-3.5 w-3.5" />} onClick={onOpenTransfer} />
          )}
          {chat?.status === 'active' && isAssignedToMe && (
            <button
              type="button"
              onClick={handleEndChat}
              disabled={ending}
              title="End this live chat"
              className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50/50 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 hover:border-red-300 disabled:opacity-40"
            >
              <LogOut className="h-3.5 w-3.5" />
              {ending ? 'Ending…' : 'End Chat'}
            </button>
          )}
        </div>
      </div>

      {finalized && (
        <div className="shrink-0 flex items-center justify-between gap-3 border-b border-green-200 bg-green-50 px-4 py-2.5">
          <p className="text-xs text-green-800">
            Conversation created: <span className="font-semibold">{finalized.referenceId}</span>
          </p>
          <button
            type="button"
            onClick={() => navigate(`/agent/conversation/view/${finalized.conversationId}`)}
            className="shrink-0 rounded-lg bg-[#3366FF] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2952CC]"
          >
            View conversation
          </button>
        </div>
      )}
      {finalizeError && (
        <div role="alert" className="shrink-0 m-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {finalizeError}
        </div>
      )}

      {error && (
        <div role="alert" className="shrink-0 m-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div
        ref={containerRef}
        aria-live="polite"
        onScroll={handleScroll}
        className="relative min-h-0 flex-1 space-y-4 overflow-y-auto thin-scrollbar bg-slate-50/50 px-5 py-5"
      >
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500" />
            Loading older messages…
          </div>
        )}
        {loading && messages.length === 0 && !error && (
          <div data-testid="chat-thread-loading" className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-200/60" />
            ))}
          </div>
        )}
        {!loading && messages.length === 0 && !error && (
          <div className="flex h-full flex-col items-center justify-center text-slate-400">
            <p className="text-sm">No messages yet.</p>
          </div>
        )}
        {messageGroups.map((group, groupIdx) => {
          const isActive = isActiveConversation(group.conversationId)
          const isCollapsed = !isActive && collapsedConversations.has(group.conversationId)
          const conversation = group.conversation
          if (isActive) {
            return group.messages.map((msg, msgIdx) =>
              isSystemOrDivider(msg) ? (
                <div
                  key={`divider-${msg.id ?? msgIdx}-${groupIdx}`}
                  className="my-5 flex items-center gap-3 px-2"
                >
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1 text-xs font-medium text-slate-600 shadow-xs">
                    {msg.body}
                  </span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              ) : (
                <MessageBubble
                  key={`m-${msg.id ?? msgIdx}-${groupIdx}`}
                  message={msg}
                  currentAgentId={currentAgentId}
                  ratingScale={ratingScale}
                  failureReason={msg.status === 'failed' ? failureReasons[msg.id] : undefined}
                />
              ),
            )
          }
          return (
            <div key={group.conversationId} className="rounded-lg border border-blue-200 bg-blue-50/50">
              <button
                type="button"
                onClick={() => toggleConversation(group.conversationId)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-blue-900"
              >
                <span>
                  Conversation created: {conversation?.referenceId ?? '—'}
                  {conversation?.subject ? ` — ${conversation.subject}` : ''}
                </span>
                {isCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
              </button>
              {!isCollapsed && (
                <div className="space-y-4 border-t border-blue-200 px-3 py-3">
                  {group.messages.map((msg, msgIdx) =>
                    isSystemOrDivider(msg) ? (
                      <div
                        key={`divider-${msg.id ?? msgIdx}-${groupIdx}`}
                        className="my-5 flex items-center gap-3 px-2"
                      >
                        <div className="h-px flex-1 bg-slate-200" />
                        <span className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1 text-xs font-medium text-slate-600 shadow-xs">
                          {msg.body}
                        </span>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                    ) : (
                      <MessageBubble
                        key={`m-${msg.id ?? msgIdx}-${groupIdx}`}
                        message={msg}
                        currentAgentId={currentAgentId}
                        ratingScale={ratingScale}
                        failureReason={msg.status === 'failed' ? failureReasons[msg.id] : undefined}
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          )
        })}

        {isVisitorTyping && !finalized && (
          <div className="flex items-center gap-1 text-xs text-slate-400" data-testid="visitor-typing">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:0.1s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:0.2s]" />
            <span className="ml-1">Visitor is typing…</span>
          </div>
        )}

        {!atBottom && newSinceAtBottom > 0 && (
          <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2">
            <button
              type="button"
              className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-[#0f172a] px-4 py-2 text-xs font-medium text-white shadow-lg"
              onClick={scrollToBottom}
            >
              {newSinceAtBottom} new
            </button>
          </div>
        )}
      </div>

      {chat?.status === 'active' && isAssignedToMe ? (
        <div className="shrink-0 pb-3">
          <MessageComposer
            conversationId={chatId}
            channelOrigin="chat"
            status="open"
            sessionExpiresAt={null}
            onSend={(_tempId, optimistic) => appendMessage(optimistic)}
            onSendSucceeded={(tempId, real) => replaceMessage(tempId, real)}
            onSendFailed={(tempId, err) => {
              markMessageFailed(tempId, err)
              setFailureReason(tempId, err)
            }}
          />
        </div>
      ) : chat && chat.status !== 'active' ? (
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500">
          This chat has ended.
        </div>
      ) : null}

      <ChatSummaryDialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        chatId={chatId}
        customerName={chat?.visitorName}
      />
    </div>
  )
}
